import el from "@cypherpotato/el";
import page from "page";
import { api } from "../../api";
import { Button, PrimaryButton } from "../../components/button";
import { Card, CardNavigation } from "../../components/card";
import { CommandBar } from "../../components/command-bar";
import { Dashboard } from "../../components/dashboard";
import { Editor, EditorDescription, EditorTitle } from "../../components/editor";
import { NumberInput, TextArea } from "../../components/form";
import { Icon } from "../../components/icon";
import { Playground, PlaygroundAside, PlaygroundAsideContainer, PlaygroundContent, PlaygroundDefault } from "../../components/playground";
import { HorizontalSizableContainer, HorizontalSizableSection, SizableContainer, SizableSection } from "../../components/sizable-container";
import { TabControl } from "../../components/tabcontrol";
import { BlockCode, BlockCodeWrapper, CheckWrapper } from "../../components/text";
import { displayAlert, displayFormControl } from "../../ui/alert";
import { formatFloat, formatNumber } from "../../ui/format";
import { runOperation } from "../../ui/operation";
import { indentWhiteSpace, valueFrom } from "../../util";

export default function (ctx) {

    const collectionId = ctx.params.id;
    const results = el.state.create(null);

    function buildJson() {
        const data = {
            term: valueFrom("documentsSearchTerm"),
            collections: [collectionId],
            minScore: valueFrom("documentsMinScore"),
            top: valueFrom("documentsMaxDocuments"),
            includeReferences: valueFrom("documentsIncludeReferences")
        };

        return data;
    }

    function doSearch() {
        runOperation("Running...", async () => {
            const response = await api.postJson(`/api/v1/query`, buildJson());
            results.value = response.data;
        })
    }

    return Dashboard(
        {
            classList: ["full-size"]
        },
        Card(
            CardNavigation(
                CommandBar(
                    PrimaryButton(Icon("play-mini-fill"), "Run query", {
                        onClick() {
                            doSearch();
                        }
                    }),
                    Button(
                        Icon("arrow-left-line"),
                        "Back to collections",
                        {
                            onClick() {
                                page("../")
                            }
                        }
                    ),
                    Button(
                        Icon("braces-line"), "View code",
                        {
                            onClick() {
                                const data = {
                                    term: valueFrom("documentsSearchTerm"),
                                    min: valueFrom("documentsMinScore"),
                                    top: valueFrom("documentsMaxDocuments")
                                };
                                displayAlert({
                                    title: "Collection code",
                                    message: el.fragment(
                                        "Use the code below to integrate the function you created.",
                                        BlockCodeWrapper("Example",
                                            `
curl '${window.env.apiEndpoint}api/v1/query' \\
    -H 'Authorization: Bearer $COMPOUND_API_KEY' \\
    -H 'Content-Type: text/plain;charset=UTF-8' \\
    -d '${indentWhiteSpace(JSON.stringify(buildJson(), null, 4), 6, true)}'
                                            `, "bash")
                                    )
                                })
                            }
                        }
                    )
                )
            ),
            Playground(
                HorizontalSizableContainer(
                    HorizontalSizableSection(
                        {
                            initialSize: "400px"
                        },
                        PlaygroundAside(
                            TabControl([
                                {
                                    title: "Parameters",
                                    icon: Icon("equalizer-2-fill"),
                                    contents: PlaygroundAsideContainer(
                                        SizableContainer(
                                            SizableSection("Search text",
                                                TextArea(null, null, {
                                                    id: "documentsSearchTerm",
                                                    placeholder: "Type your query..."
                                                })
                                            ),
                                            SizableSection("Parameters",
                                                Editor(
                                                    {
                                                        id: "documentsMinScore",
                                                        value: 0.4,
                                                        renderValue(value) {
                                                            return Button(formatFloat(value));
                                                        },
                                                        manipulateValue(value, callback) {
                                                            displayFormControl({
                                                                title: this.editorTitle,
                                                                message: this.editorParagraphs,
                                                                formControlItem: NumberInput({ value, min: 0.01, max: 1, step: 0.05 }),
                                                                onConfirm: callback,
                                                                validate: []
                                                            })
                                                        }
                                                    },
                                                    EditorDescription(
                                                        EditorTitle("Minimum score")
                                                    )
                                                ),
                                                Editor(
                                                    {
                                                        id: "documentsMaxDocuments",
                                                        value: 4,
                                                        renderValue(value) {
                                                            return Button(formatNumber(value));
                                                        },
                                                        manipulateValue(value, callback) {
                                                            displayFormControl({
                                                                title: this.editorTitle,
                                                                message: this.editorParagraphs,
                                                                formControlItem: NumberInput({ value, min: 1, max: 100, step: 1 }),
                                                                onConfirm: callback,
                                                                validate: []
                                                            })
                                                        }
                                                    },
                                                    EditorDescription(
                                                        EditorTitle("Maximum results")
                                                    )
                                                ),
                                                Editor(
                                                    {
                                                        id: "documentsIncludeReferences",
                                                        value: true,
                                                        renderValue(value) {
                                                            return CheckWrapper(value, "On", "Off");
                                                        },
                                                        manipulateValue(value, callback) {
                                                            callback(!!!value);
                                                        }
                                                    },
                                                    EditorDescription(
                                                        EditorTitle("Include chunked references")
                                                    )
                                                )
                                            )
                                        )
                                    )
                                }
                            ], {
                                type: "vertical"
                            })
                        )
                    ),
                    HorizontalSizableSection(
                        {
                            initialSize: "calc(100% - 400px)"
                        },
                        el.state.listening([results], () =>
                            PlaygroundContent(
                                results.value == null && PlaygroundDefault("play-large-line", "Click the button in the left to run a query."),
                                results.value && BlockCode(JSON.stringify(results.value, null, 2), "json")
                            )
                        )
                    )
                )
            )
        )
    )
}
